import { CommonModule, DatePipe } from '@angular/common';
import { Component, inject, OnDestroy } from '@angular/core';
import { StateService } from '@uirouter/core';
import { Subscription } from 'rxjs';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatTooltipModule } from '@angular/material/tooltip';

import { PresentationApiService } from 'src/app/api/services/presentation-api.service';
import { ModalService } from 'src/app/components/modals/modal.service';
import { FeaturesService } from 'src/app/components/plans/features.service';
import { ComponentsModule } from 'src/app/components/components.module';
import {
  AccessControlList,
  ShareWithUsersModalComponent,
} from 'src/app/components/share-with-users-modal/share-with-users-modal.component';
import { StatusBadgeComponent } from 'src/app/components/status-badge/status-badge.component';
import { FocusOutlineDirective } from 'src/app/accessibility/directives/focus-outline/focus-outline.directive';
import { SharedModule } from 'src/app/shared/shared.module';
import { UsernamePipe } from 'src/app/shared/pipes/username.pipe';
import { ComponentsService } from '../../services/components.service';
import { TemplateEditorService } from '../../services/template-editor.service';
import { PresentationOrientationSelectorComponent } from '../presentation-orientation-selector/presentation-orientation-selector.component';
import { ScheduleSelectorService } from 'src/app/schedules/services/schedule-selector.service';
import { PermissionService } from 'src/app/auth/services/permission.service';
import { UserStateService } from 'src/app/auth/services/user-state.service';
import { SCHEDULE_ACCESS } from 'src/app/auth/types/access-level';
import { BroadcasterService } from 'src/app/shared/services/broadcaster.service';

@Component({
  selector: 'template-editor-toolbar',
  templateUrl: './template-editor-toolbar.component.html',
  styleUrls: ['./template-editor-toolbar.component.scss'],
  host: {
    class: 'tw-flex tw-flex-1 tw-min-w-0 tw-items-center',
  },
  imports: [
    CommonModule,
    ComponentsModule,
    FocusOutlineDirective,
    MatButtonModule,
    MatIconModule,
    MatMenuModule,
    MatTooltipModule,
    PresentationOrientationSelectorComponent,
    SharedModule,
    StatusBadgeComponent,
  ],
  providers: [DatePipe],
})
export class TemplateEditorToolbarComponent implements OnDestroy {
  protected isPublicationApprovalAvailable = false;
  protected isShareWithOthersAvailable = false;
  public schedulesComponent;
  private datePipe = inject(DatePipe);
  private usernamePipe = inject(UsernamePipe);
  private broadcaster = inject(BroadcasterService);
  private presentationLoadedSubscription?: Subscription;

  constructor(
    private modalService: ModalService,
    public templateEditorFactory: TemplateEditorService,
    public componentsService: ComponentsService,
    private featuresService: FeaturesService,
    private scheduleSelectorService: ScheduleSelectorService,
    public permissionService: PermissionService,
    public userStateService: UserStateService,
    private stateService: StateService,
    private presentationApiService: PresentationApiService,
  ) {
    this.isPublicationApprovalAvailable =
      this.featuresService.isFeatureAvailable(
        FeaturesService.FEATURE_PUBLICATION_APPROVAL,
      );
    this.isShareWithOthersAvailable = this.featuresService.isFeatureAvailable(
      FeaturesService.FEATURE_GRANULAR_USER_PERMISSIONS,
    );

    if (
      this.permissionService.hasAccess(SCHEDULE_ACCESS.PLAYLIST_MANAGER) ||
      this.userStateService.isAssignedPublisher() ||
      this.templateEditorFactory.canPublishPresentation()
    ) {
      this.schedulesComponent =
        this.scheduleSelectorService.getSchedulesComponent(
          () => this.templateEditorFactory.presentation,
        );
      this.presentationLoadedSubscription = this.broadcaster.on(
        'risevision.template-editor.presentationLoaded',
        () => this.reloadSchedulesForCurrentPresentation(),
      );
    }
  }

  ngOnDestroy(): void {
    this.presentationLoadedSubscription?.unsubscribe();
  }

  private reloadSchedulesForCurrentPresentation(): void {
    if (!this.templateEditorFactory.presentation?.id) {
      return;
    }

    this.scheduleSelectorService.loadSelectedSchedules();
  }

  newPresentation(): void {
    this.stateService.go('apps.editor.create');
  }

  confirmDelete() {
    this.modalService
      .confirmDanger(
        'Are you sure you want to delete this Presentation?',
        null,
        'Delete Forever',
      )
      .then(() => {
        this.templateEditorFactory.deletePresentation();
      });
  }

  openSettings() {
    let page = this.componentsService.getCurrentPage();
    if (!page || page.type !== 'rise-template-share-settings') {
      this.componentsService.editComponent({
        type: 'rise-template-share-settings',
      });
    }
  }

  editSchedules(): void {
    if (this.schedulesComponent) {
      this.scheduleSelectorService.openDialog();
    }
  }

  shareWithOthers(): Promise<void> {
    return this.featuresService
      .confirmShareWithOthersAvailable()
      .then(() => {
        const presentation = this.templateEditorFactory.presentation;
        const accessControlList =
          this.presentationApiService.getAccessControlList(presentation.id);

        return this.modalService.showMediumDialog(
          ShareWithUsersModalComponent,
          {
            accessControlList,
            accessLevels: ['Editor', 'Publisher', 'Full Access'],
            defaultAccessLevel: 'Editor',
            saveAccessControlList: async (
              updatedAccessControlList: AccessControlList,
              comment?: string,
            ) => {
              const response =
                await this.presentationApiService.updateAccessControlList(
                  presentation.id,
                  updatedAccessControlList as NonNullable<
                    typeof presentation.accessControlList
                  >,
                  comment,
                );
              presentation.accessControlList =
                response.item?.accessControlList ?? updatedAccessControlList;
            },
          },
          { width: '640px', maxWidth: 'calc(100vw - 32px)' },
        );
      })
      .catch(() => {});
  }

  getSavedTooltipText() {
    return `Saved ${this.datePipe.transform(
      this.templateEditorFactory.presentation.changeDate,
      'MMM d yyyy h:mm a',
    )} by ${this.usernamePipe.transform(
      this.templateEditorFactory.presentation.changedBy,
    )}`;
  }
}
